import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Tooltip,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { API_BASE_URL } from '../../constants/constants';
import { usePermissions } from '../../context/PermissionContext';


// [CASH-MODE] Withdrawal source labels: cashback = UPI rewards, cash = Cash rewards (cash-payment game cards)
const SOURCE_META = {
  cashback: { label: 'UPI Rewards', bg: '#fff7ed', color: '#ea580c', balance: 'VPA Total' },
  referral: { label: 'Referral', bg: '#ede9fe', color: '#7c3aed', balance: 'Referral' },
  cash: { label: 'Cash Rewards', bg: '#dcfce7', color: '#15803d', balance: 'Cash Rewards' },
};
const sourceMeta = (source) => SOURCE_META[source] || SOURCE_META.cashback;

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;
const copyText = (text) => {
  try { navigator.clipboard.writeText(String(text)); } catch (e) { /* clipboard not available */ }
};

// Bank details block: name, account number, IFSC (+ bank). Used in the list and in the action dialog.
const BankDetails = ({ bank, compact }) => {
  if (!bank) return <Typography variant="caption" color="text.secondary">No bank details (older request)</Typography>;
  const line = (label, value, copy) => (
    <Typography variant={compact ? 'caption' : 'body2'} component="div" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span style={{ color: '#64748b' }}>{label}:</span> <strong style={{ fontFamily: label === 'A/C' || label === 'IFSC' ? 'monospace' : undefined }}>{value || '-'}</strong>
      {copy && value && (
        <Tooltip title={`Copy ${label}`}>
          <IconButton size="small" onClick={() => copyText(value)} sx={{ p: 0.25 }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>
        </Tooltip>
      )}
    </Typography>
  );
  return (
    <Box>
      {line('Name', bank.holderName)}
      {line('A/C', bank.accountNumber, !String(bank.accountNumber).startsWith('XXXX'))}
      {line('IFSC', bank.ifsc, true)}
      {bank.bankName && line('Bank', bank.bankName)}
    </Box>
  );
};

// request amount = fee + net (to transfer)
const AmountBreakdown = ({ w }) => (
  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5, bgcolor: '#f8fafc' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">Request amount</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{money(w.totalAmount ?? w.amount)}</Typography>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">Transaction fee</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309' }}>− {money(w.feeAmount)}</Typography>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #cbd5e1', mt: 0.5, pt: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>Amount to transfer</Typography>
      <Typography variant="body1" sx={{ fontWeight: 800, color: 'success.dark' }}>{money(w.netAmount ?? w.amount)}</Typography>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary">Total deducted from customer balance</Typography>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>{money(w.totalAmount ?? w.amount)}</Typography>
    </Box>
  </Box>
);

const WithdrawalManagement = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filteredWithdrawals, setFilteredWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: '' });
  const [actionData, setActionData] = useState({ note: '', transactionId: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { can } = usePermissions();
  const showSensitive = can('smartPoint.withdrawals.viewSensitive'); // mobile / VPA hidden in the list without it

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchWithdrawals = useCallback(async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/withdrawals/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWithdrawals(response.data.withdrawals || []);
    } catch (error) {
      showSnackbar('Failed to fetch withdrawals', 'error');
      console.error('Error fetching withdrawals:', error);
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const filterWithdrawals = useCallback(() => {
    let filtered = withdrawals;
    if (statusFilter !== 'all') {
      filtered = filtered.filter((w) => w.status === statusFilter);
    }
    if (sourceFilter !== 'all') {
      filtered = filtered.filter((w) => (w.source || 'cashback') === sourceFilter);
    }
    setFilteredWithdrawals(filtered);
  }, [withdrawals, statusFilter, sourceFilter]);

  useEffect(() => {
    // Page access is enforced by the router permission guard
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  useEffect(() => {
    filterWithdrawals();
  }, [filterWithdrawals]);

  const handleOpenDialog = (withdrawal, action) => {
    if (action === 'processing' ? !can('smartPoint.withdrawals.process') : !can('smartPoint.withdrawals.complete')) return;
    setSelectedWithdrawal(withdrawal);
    setActionDialog({ open: true, action });
    setActionData({ note: '', transactionId: '' });
  };

  const handleCloseDialog = () => {
    setActionDialog({ open: false, action: '' });
    setSelectedWithdrawal(null);
    setActionData({ note: '', transactionId: '' });
  };

  const handleMarkAsProcessing = async () => {
    if (!can('smartPoint.withdrawals.process')) return;
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/withdrawals/${selectedWithdrawal._id}/processing`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar('Withdrawal marked as processing');
      fetchWithdrawals();
      handleCloseDialog();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to update withdrawal', 'error');
    }
  };

  const handleCompleteWithdrawal = async () => {
    if (!can('smartPoint.withdrawals.complete')) return;
    if (!actionData.transactionId.trim()) {
      showSnackbar('Transaction ID is required', 'warning');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/withdrawals/${selectedWithdrawal._id}/complete`,
        {
          transactionId: actionData.transactionId,
          note: actionData.note,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar('Withdrawal completed successfully');
      fetchWithdrawals();
      handleCloseDialog();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to complete withdrawal', 'error');
    }
  };

  const handleFailWithdrawal = async () => {
    if (!can('smartPoint.withdrawals.complete')) return;
    if (!actionData.note.trim()) {
      showSnackbar('Failure reason is required', 'warning');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/withdrawals/${selectedWithdrawal._id}/fail`,
        { note: actionData.note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar('Withdrawal marked as failed');
      fetchWithdrawals();
      handleCloseDialog();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to update withdrawal', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                backgroundColor: 'rgba(255, 165, 0, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  VPA Withdrawal Management
                </Typography>
                <Chip
                  label={`${filteredWithdrawals.length} Requests`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700, borderRadius: 1.5 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Monitor, process, and complete customer VPA wallet withdrawal requests
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => setSourceFilter(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Sources</MenuItem>
                <MenuItem value="cashback">UPI Rewards</MenuItem>
                <MenuItem value="referral">Referral</MenuItem>
                <MenuItem value="cash">Cash Rewards</MenuItem>{/* [CASH-MODE] */}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Withdrawals</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              color="primary"
              onClick={fetchWithdrawals}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Request Date</TableCell>
              {showSensitive && <TableCell sx={{ fontWeight: 700 }}>Customer Mobile</TableCell>}
              {showSensitive && <TableCell sx={{ fontWeight: 700 }}>VPA ID</TableCell>}
              <TableCell align="right" sx={{ fontWeight: 700 }}>Request (₹)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Fee (₹)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>To Transfer (₹)</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Bank Details</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Processed Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredWithdrawals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showSensitive ? 11 : 9} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                    No withdrawals found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No withdrawal requests match your current status filter
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredWithdrawals.map((withdrawal) => (
                <TableRow key={withdrawal._id}>
                  <TableCell sx={{ fontWeight: 500 }}>{formatDate(withdrawal.requestedAt)}</TableCell>
                  {showSensitive && (
                    <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{withdrawal.customerId?.customerMobile || 'N/A'}</TableCell>
                  )}
                  {showSensitive && (
                    <TableCell>
                      <Chip label={withdrawal.vpaId} size="small" variant="outlined" sx={{ fontWeight: 600, borderRadius: 1.5 }} />
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{money(withdrawal.totalAmount ?? withdrawal.amount)}</TableCell>
                  <TableCell align="right" sx={{ color: '#b45309' }}>{money(withdrawal.feeAmount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: 'success.dark', fontSize: '0.95rem' }}>
                    {money(withdrawal.netAmount ?? withdrawal.amount)}
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}><BankDetails bank={withdrawal.bank} compact /></TableCell>
                  <TableCell>
                    <Chip
                      label={sourceMeta(withdrawal.source).label}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        borderRadius: 1.5,
                        backgroundColor: sourceMeta(withdrawal.source).bg,
                        color: sourceMeta(withdrawal.source).color,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={withdrawal.status.toUpperCase()}
                      color={getStatusColor(withdrawal.status)}
                      size="small"
                      sx={{ fontWeight: 700, borderRadius: 1.5 }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{formatDate(withdrawal.processedAt)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      {withdrawal.status === 'pending' && can('smartPoint.withdrawals.process') && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleOpenDialog(withdrawal, 'processing')}
                          sx={{ borderRadius: 1.5, fontWeight: 600 }}
                        >
                          Mark Processing
                        </Button>
                      )}

                      {withdrawal.status === 'processing' && can('smartPoint.withdrawals.complete') && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleOpenDialog(withdrawal, 'complete')}
                            sx={{ borderRadius: 1.5, fontWeight: 600 }}
                          >
                            Complete
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleOpenDialog(withdrawal, 'fail')}
                            sx={{ borderRadius: 1.5, fontWeight: 600 }}
                          >
                            Mark Failed
                          </Button>
                        </>
                      )}

                      {withdrawal.note && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', width: '100%', fontStyle: 'italic' }}>
                          Note: {withdrawal.note}
                        </Typography>
                      )}

                      {withdrawal.transactionId && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', width: '100%', fontWeight: 600 }}>
                          TXN: {withdrawal.transactionId}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionDialog.action === 'processing' && 'Mark as Processing'}
          {actionDialog.action === 'complete' && 'Complete Withdrawal'}
          {actionDialog.action === 'fail' && 'Mark as Failed'}
        </DialogTitle>
        <DialogContent>
          {selectedWithdrawal && (
            <Box sx={{ mb: 2 }}>
              {(showSensitive || (actionDialog.action === 'complete' && selectedWithdrawal.payee)) ? (
                <>
                  <Typography variant="body2">
                    <strong>Customer:</strong> {showSensitive ? selectedWithdrawal.customerId?.customerMobile : selectedWithdrawal.payee?.customerMobile}
                  </Typography>
                  <Typography variant="body2">
                    <strong>VPA:</strong> {showSensitive ? selectedWithdrawal.vpaId : selectedWithdrawal.payee?.vpaId}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2">
                  <strong>Customer:</strong> {selectedWithdrawal.customerId?.customerName || '-'}
                </Typography>
              )}
              <Box sx={{ my: 1.5 }}><AmountBreakdown w={selectedWithdrawal} /></Box>
              {selectedWithdrawal.bank && (
                <Box sx={{ mb: 1.5, p: 1.5, border: '1px solid #bbf7d0', borderRadius: 2, bgcolor: '#f0fdf4' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#166534', display: 'block', mb: 0.5 }}>
                    TRANSFER {money(selectedWithdrawal.netAmount ?? selectedWithdrawal.amount)} TO THIS BANK ACCOUNT
                  </Typography>
                  <BankDetails bank={selectedWithdrawal.bank} />
                </Box>
              )}
              <Typography variant="body2">
                <strong>Source:</strong>{' '}
                <Chip
                  label={sourceMeta(selectedWithdrawal.source).label}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    backgroundColor: sourceMeta(selectedWithdrawal.source).bg,
                    color: sourceMeta(selectedWithdrawal.source).color,
                  }}
                />
              </Typography>
            </Box>
          )}

          {actionDialog.action === 'processing' && (
            <Alert severity="info">
              This will mark the withdrawal as "Processing" and assign it to you. Please transfer{' '}
              <strong>{money(selectedWithdrawal?.netAmount ?? selectedWithdrawal?.amount)}</strong>{' '}
              {selectedWithdrawal?.bank ? "to the customer's bank account shown above" : "to the customer's VPA"}.
            </Alert>
          )}

          {actionDialog.action === 'complete' && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                This will mark the withdrawal as completed and deduct the amount from the customer's {sourceMeta(selectedWithdrawal?.source).balance} balance.
              </Alert>
              <TextField
                fullWidth
                label="Bank Transaction ID / UTR"
                value={actionData.transactionId}
                onChange={(e) => setActionData({ ...actionData, transactionId: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Note (optional)"
                value={actionData.note}
                onChange={(e) => setActionData({ ...actionData, note: e.target.value })}
                multiline
                rows={2}
              />
            </>
          )}

          {actionDialog.action === 'fail' && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will mark the withdrawal as failed. The amount stays in the customer's balance (no fee is charged).
              </Alert>
              <TextField
                fullWidth
                label="Failure Reason"
                value={actionData.note}
                onChange={(e) => setActionData({ ...actionData, note: e.target.value })}
                required
                multiline
                rows={3}
                placeholder="Enter the reason for failure (e.g., Invalid VPA, Bank error, etc.)"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={
              actionDialog.action === 'processing'
                ? handleMarkAsProcessing
                : actionDialog.action === 'complete'
                ? handleCompleteWithdrawal
                : handleFailWithdrawal
            }
            variant="contained"
            color={actionDialog.action === 'fail' ? 'error' : 'primary'}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WithdrawalManagement;
